import type { ReactNode } from "react";
import { CheckCircle2, Copy, Phone, QrCode } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { Button, ErrorBlock, Price, Spinner } from "../../components/ui.tsx";
import { usePay } from "../../lib/use-pay.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 收款说明 / 收银台
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **付款是单任务页，网页上也不该铺满 1280。**
 *    这一页只回答三个问题：**给谁、给多少、然后呢**。
 *    横着摊开只会让这三样离得更远 —— 所以居中一栏，宽度收在 640。
 *    （不是所有网页版都等于"更宽"，这一页的正确形态就是窄的。）
 *
 * 🔴 **「怎么走」在网页上排成横向三步。**
 *    手机上是三行文字，网页上一排三格 —— 用户一眼看到"这件事总共三步、
 *    我现在在第一步"，而线下收款最大的问题就是买家不知道流程有多长。
 *
 * ⚠️ 线上付款链接在电脑上是**要用手机扫或复制到手机打开的**，
 *    所以网页版把链接做成可复制的一整行，而不是照抄手机版那个"去付款"按钮
 *    —— 在电脑上点它跳到支付宝网页版，多数人还是得掏手机。
 */
export function DesktopPay() {
  const p = usePay();
  const { instruction, result, poll } = p;

  if (p.orderIds.length === 0)
    return (
      <Frame>
        <ErrorBlock error={new Error("没有要支付的订单")} />
      </Frame>
    );

  if (p.done)
    return (
      <Frame>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card py-16">
          <CheckCircle2 className="size-12 text-emerald-500" />
          <p className="text-[15px]">支付成功，正在返回订单…</p>
        </div>
      </Frame>
    );

  /**
   * ── 线下收款 ───────────────────────────────────────────────
   * 🔴 **这个商城不走线上支付**：线上只建订单，收款走别的渠道。
   */
  if (p.offline && instruction.data) {
    const inst = instruction.data;

    /** 商家确认收款之后这一页自己会变 —— 不用他刷新，也不用他再点一次。 */
    if (inst.payment !== "pending")
      return (
        <Frame>
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card py-16">
            <CheckCircle2 className="size-12 text-emerald-500" />
            <p className="text-[15px]">商家已确认收款，正在备货</p>
            <Button className="mt-2" onClick={p.goOrders}>
              查看订单
            </Button>
          </div>
        </Frame>
      );

    return (
      <Frame>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {/* ── 给多少 ── */}
          <div className="border-b border-border px-8 py-7 text-center">
            <p className="text-[14px] text-muted-foreground">需付款</p>
            <p className="mt-2">
              <Price cents={inst.payableAmount} className="text-[40px] text-brand-600" />
            </p>
          </div>

          {/* ── 给谁 ── */}
          <div className="border-b border-border px-8 py-6">
            <p className="mb-3 text-[14px] text-muted-foreground">把货款付给</p>
            <div className="flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[18px] font-medium">{inst.payee.name}</p>
                {/*
                  ⚠️ **只给联系方式，不给收款账号。**
                     账号变更频繁（换卡、换收款码），存进库里过期了
                     就是把钱打给一个作废的账户；而且落库意味着任何能读
                     商家资料的人都能拿到他的账户。让双方直接联系最稳。
                */}
                {inst.payee.contactPhone ? (
                  <p className="mt-1.5 flex items-center gap-2 text-[18px] text-brand-600 tabular-nums">
                    <Phone className="size-[18px]" />
                    {inst.payee.contactPhone}
                    {inst.payee.contactName && (
                      <span className="text-[14px] text-muted-foreground">
                        （{inst.payee.contactName}）
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="mt-1.5 text-[14px] text-muted-foreground">
                    联系方式还没填，请到店铺页或联系客服获取
                  </p>
                )}
              </div>
              {/* ⚠️ 电脑上没法点号码拨出去，所以给复制按钮 —— 手机版是 tel: 链接。 */}
              {inst.payee.contactPhone && (
                <Button
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard
                      ?.writeText(inst.payee.contactPhone)
                      .catch(() => {});
                  }}
                >
                  <Copy className="size-4" />
                  复制号码
                </Button>
              )}
            </div>
          </div>

          {/* ── 然后呢 ── */}
          <div className="px-8 py-6">
            <p className="mb-4 text-[14px] text-muted-foreground">接下来</p>
            <ol className="grid grid-cols-3 gap-4">
              {[
                { n: "1", t: "联系上面这位付款", d: "转账 / 微信 / 现金 / 对公都行" },
                { n: "2", t: "他在后台确认收款", d: "确认之后这一页会自己变" },
                { n: "3", t: "开始备货发货", d: "物流单号会出现在订单详情里" },
              ].map((s) => (
                <li key={s.n} className="rounded-lg bg-secondary/50 p-4">
                  <span className="flex size-7 items-center justify-center rounded-full bg-brand-600 text-[13px] font-medium text-white">
                    {s.n}
                  </span>
                  <p className="mt-2.5 text-[14px]">{s.t}</p>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{s.d}</p>
                </li>
              ))}
            </ol>

            {inst.expireAt && (
              /*
                ⚠️ 超时会释放库存，必须说在前面 ——
                   不说的话买家钱都转出去了才发现单子没了。
              */
              <p className="mt-5 rounded-lg bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
                请在 {new Date(inst.expireAt).toLocaleString("zh-CN", { hour12: false })} 前完成，
                逾期订单会自动取消并释放库存。
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-center">
          <Button variant="outline" onClick={p.goOrders}>
            我知道了，去订单列表
          </Button>
        </div>
      </Frame>
    );
  }

  if (instruction.isLoading)
    return (
      <Frame>
        <Spinner label="正在确认付款方式" compact />
      </Frame>
    );

  /** ── 线上收款（走中转站网关时） ── */
  return (
    <Frame>
      <div className="rounded-xl border border-border bg-card p-6">
        {p.create.isPending && <Spinner label="正在创建收款单" compact />}
        {p.create.error && <ErrorBlock error={p.create.error} onRetry={() => p.create.mutate()} />}
        {result && !result.success && <ErrorBlock error={new Error(result.error)} />}

        {result?.success && !result.split && (
          <>
            <p className="mb-4 flex items-center gap-2 rounded-lg bg-brand-50 px-4 py-3 text-[13px] text-brand-700">
              <QrCode className="size-4 shrink-0" />
              在电脑上付款请把下面的链接复制到手机里打开，付完这一页会自动跳转
            </p>
            <PayRow payUrl={result.payUrl} label="支付宝 / 微信" />
            {result.wechatPayUrl && <PayRow payUrl={result.wechatPayUrl} label="微信" />}
          </>
        )}

        {result?.success && result.split && (
          <>
            {/*
              🔴 必须说清楚还要付几笔，否则用户付完第一笔就走了，货永远发不出去。
              ⚠️ 续付时列出来的只是**没付的那几笔**，标题要跟着变。
            */}
            <div className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
              <p className="font-medium">
                {result.subOrders.length < result.splitTotal
                  ? `这一单分 ${result.splitTotal} 笔付，还差 ${result.subOrders.length} 笔`
                  : `这笔金额超过了单次收款上限，需要分 ${result.splitTotal} 笔付款`}
              </p>
              <p className="mt-0.5">
                <strong>付满整组才会发货</strong>
                。中途关掉也没关系，回到订单页可以接着付，已经付进去的不会作废。
              </p>
            </div>
            {result.subOrders.map((sub, i) => (
              <PayRow
                key={sub.orderNo}
                payUrl={sub.payUrl}
                label={`第 ${i + 1} 笔`}
                amount={sub.uniqueAmountCents}
              />
            ))}
          </>
        )}

        {poll.data && (
          <p className="mt-5 text-center text-[13px] text-muted-foreground">
            {poll.data.groupTotal !== undefined
              ? `已付 ${p.paidCount}/${poll.data.groupTotal} 笔，付满才会发货…`
              : poll.data.status === "processing"
                ? "钱已收到，正在处理…"
                : "等待付款…"}
          </p>
        )}
      </div>
    </Frame>
  );
}

/**
 * ⚠️ **必须定义在组件外面。** 写在组件体里的话每次渲染都是一个新组件类型，
 *    React 会把整棵子树卸载重建 —— 顶栏的搜索框每渲染一次就被清空一次。
 */
function Frame({ children }: { children: ReactNode }) {
  return (
    <DesktopShell>
      <Crumbs
        items={[{ label: "首页", to: "/" }, { label: "订单", to: "/orders" }, { label: "付款" }]}
      />
      <div className="mx-auto max-w-[640px]">{children}</div>
    </DesktopShell>
  );
}

/** 一笔待付的钱。⚠️ 电脑上主操作是**复制链接到手机**，不是当场跳转。 */
function PayRow({ payUrl, label, amount }: { payUrl: string; label: string; amount?: number }) {
  return (
    <div className="mb-3 rounded-lg border border-border p-4 last:mb-0">
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1 text-[14px]">
          {label}
          {amount !== undefined && (
            <span className="ml-2">
              <Price cents={amount} className="text-brand-600" />
            </span>
          )}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void navigator.clipboard?.writeText(payUrl).catch(() => {});
          }}
        >
          <Copy className="size-3.5" />
          复制链接
        </Button>
        <Button size="sm" onClick={() => window.location.assign(payUrl)}>
          在本机打开
        </Button>
      </div>
      <p className="mt-2 truncate font-mono text-[12px] text-muted-foreground">{payUrl}</p>
    </div>
  );
}
